import { GetEcheanceGlobalInfoResponse, PaymentScheduleHistoryItemDto } from "api/payment-schedule";
import { EntityDetailsHeader, EntityDetailsHeaderConfig } from "components/display/EntityDetailsHeader";
import { EntityDetailsStat, EntityDetailsStatConfig } from "components/display/EntityDetailsStat";
import RoutedTabs from "components/navigation/Tabs/RoutedTabs";
import { TabItem } from "components/navigation/Tabs/TabItem";
import { TabContent } from "components/navigation/Tabs/TabContent";
import { Calendar, CreditCard, FileText, Hash, Info, PieChart } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { paymentScheduleAPI } from "services/swagger-api";
import { formatAmount, formatDate, getStatusInfo, StatusBadge } from "./PaymentScheduleInfoTab";
import { PaymentScheduleInfoTab } from "./PaymentScheduleInfoTab";
import { PaymentScheduleBreakdownTab } from "./PaymentScheduleBreakdownTab";

export const PaymentScheduleDetails: FC = () => {
  const { paymentScheduleId } = useParams<{ paymentScheduleId: string }>();
  const navigate = useNavigate();
  const [globalInfo, setGlobalInfo] = useState<GetEcheanceGlobalInfoResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGlobalInfo = async () => {
      if (!paymentScheduleId) return;
      try {
        setIsLoading(true);
        setError(null);
        const data = await paymentScheduleAPI.getPaymentScheduleGlobalInfo({ paymentScheduleId });
        setGlobalInfo(data);
      } catch (err) {
        console.error("Erreur lors du chargement des informations globales:", err);
        setError("Impossible de charger les informations du prélèvement");
      } finally {
        setIsLoading(false);
      }
    };
    fetchGlobalInfo();
  }, [paymentScheduleId]);

  if (isLoading || !paymentScheduleId) {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl p-8">
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Chargement...</p>
        </div>
      </section>
    );
  }

  if (error || !globalInfo) {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl p-8">
        <div className="flex items-center justify-center h-64">
          <p className="text-red-500">{error || "Prélèvement introuvable"}</p>
        </div>
      </section>
    );
  }

  const statusInfo = getStatusInfo(globalInfo.status);
  const subscriberName = `${globalInfo.subscriber.lastName ?? ""} ${globalInfo.subscriber.firstName ?? ""}`.trim() || "Souscripteur inconnu";

  const headerConfig: EntityDetailsHeaderConfig = {
    icon: <CreditCard className="text-primary" />,
    title: subscriberName,
    subtitle: `Échéance n°${globalInfo.paymentNumber} — ${globalInfo.contractNumber}`,
    infoSections: [
      { icon: FileText, label: "PRODUIT", value: globalInfo.productName, disabled: true },
      { icon: Calendar, label: "ÉCHÉANCE", value: formatDate(globalInfo.dueDate), disabled: true },
      { icon: CreditCard, label: "MONTANT", value: formatAmount(globalInfo.amount), disabled: true },
    ],
    badge: {
      label: statusInfo.label,
      variant: statusInfo.variant,
      icon: CreditCard,
    },
    metadata: {
      icon: Calendar,
      text: `Statut mis à jour le ${formatDate(globalInfo.statusDate)}`,
    },
  };

  const statsConfig: EntityDetailsStatConfig = {
    stats: [
      { label: "Montant total", value: formatAmount(globalInfo.amount) },
      { label: "Prime pure", value: formatAmount(globalInfo.purePremium) },
      { label: "Commissions", value: formatAmount(globalInfo.commissions) },
      { label: "Taxes", value: formatAmount(globalInfo.tax) },
      { label: "Frais", value: formatAmount(globalInfo.fees) },
      { label: "Statut", value: statusInfo.label },
      // Libellé déjà prêt côté back : « Comptant », « Terme », « Sinistre »
      // ou « Résiliation ».
      { label: "Type de quittance", value: globalInfo.receiptType || "—" },
    ],
  };

  return (
    <section className="w-full overflow-hidden border bg-white rounded-2xl">
      <EntityDetailsHeader config={headerConfig} />

      <div className="p-8 pb-0 shadow-inner">
        <EntityDetailsStat config={statsConfig} />
      </div>

      <div className="flex gap-6 px-8 pb-8 pt-8">
        {/* Side menu - payment history */}
        {globalInfo.contractPaymentHistory.length > 0 && (
          <div className="flex-shrink-0 w-72">
            <div className="bg-primary/5 rounded-3xl shadow-sm overflow-hidden sticky top-6 border border-primary/10">
              <div className="px-4 pt-4 pb-2">
                <h3 className="text-sm font-bold text-primary">Échéances du contrat</h3>
              </div>
              <nav className="p-2 pt-0 space-y-0.5 max-h-[calc(100vh-16rem)] overflow-y-auto">
                {globalInfo.contractPaymentHistory.map((item: PaymentScheduleHistoryItemDto, index: number) => (
                  <button
                    key={item.id}
                    onClick={() => navigate(`/back-office/prelevements/${item.id}?tab=info`)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-3 text-left transition-all duration-300 group rounded-2xl
                      ${item.id === paymentScheduleId
                        ? "bg-primary/10"
                        : "hover:bg-primary/5"
                      }
                      ${index !== globalInfo.contractPaymentHistory.length - 1 ? "border-b border-primary/5" : ""}
                    `}
                  >
                    <div className={`flex-shrink-0 transition-all duration-300 ${
                      item.id === paymentScheduleId
                        ? "text-primary transform scale-110"
                        : "text-gray-500 group-hover:text-primary group-hover:transform group-hover:scale-110"
                    }`}>
                      <Hash className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm truncate transition-all duration-300 ${
                          item.id === paymentScheduleId
                            ? "text-primary font-bold"
                            : "text-gray-500 font-semibold group-hover:text-primary"
                        }`}>
                          N°{item.paymentNumber}
                        </span>
                        <StatusBadge status={item.status} statusColor={item.statusColor} />
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <span className={`text-[11px] transition-all duration-300 ${
                          item.id === paymentScheduleId ? "text-primary/60" : "text-gray-400 group-hover:text-primary/50"
                        }`}>{formatDate(item.dueDate)}</span>
                        <span className={`text-[11px] font-medium transition-all duration-300 ${
                          item.id === paymentScheduleId ? "text-primary" : "text-gray-500 group-hover:text-primary"
                        }`}>{formatAmount(item.amount)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Main content with tabs */}
        <div className="flex-1 min-w-0">
          <RoutedTabs paramName="tab">
            <TabItem label="Informations" value="info" icon={<Info size={16} />} />
            <TabItem label="Répartition" value="repartition" icon={<PieChart size={16} />} />

            <TabContent value="info">
              <PaymentScheduleInfoTab paymentScheduleId={paymentScheduleId} />
            </TabContent>

            <TabContent value="repartition">
              <PaymentScheduleBreakdownTab paymentScheduleId={paymentScheduleId} />
            </TabContent>
          </RoutedTabs>
        </div>
      </div>
    </section>
  );
};
